import { useEffect, useRef, useState } from "react";

import { Loader2, Play } from "lucide-react";

import { listVoices, synthesizeSpeech, type VoiceOption } from "../../api/voices";
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui";

/** Sample line spoken when previewing a voice. */
const PREVIEW_TEXT =
  "Hi — this is how I sound. I can read your messages aloud like this.";

interface VoicePickerProps {
  /** ws(s):// voice service URL; REST base is derived from it. */
  voiceApiUrl?: string;
  /** Current voice id, or null/undefined for "inherit / default". */
  value: string | null | undefined;
  /** Called with the chosen voice id, or null when "Default" is picked. */
  onChange: (voice: string | null) => void;
  /** Label shown above the picker. */
  label?: string;
  /**
   * Text for the "default/inherit" option. Persona builder → "Default voice";
   * chat settings → the resolved default's name.
   */
  defaultOptionLabel?: string;
}

/**
 * Voice selector with an inline preview button (tdd-0044). Lists the s2s voice
 * catalog, lets the user pick one (or "default"), and plays a sample line via
 * POST /v1/audio/speech. Disabled gracefully when the voice service is absent.
 */
export function VoicePicker({
  voiceApiUrl,
  value,
  onChange,
  label = "Voice",
  defaultOptionLabel = "Default voice",
}: VoicePickerProps) {
  const [voices, setVoices] = useState<VoiceOption[]>([]);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!voiceApiUrl) {
      setVoices([]);
      return;
    }
    listVoices(voiceApiUrl).then((list) => {
      if (active) setVoices(list);
    });
    return () => {
      active = false;
    };
  }, [voiceApiUrl]);

  // Clean up any object URL + audio element on unmount.
  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, []);

  const previewVoice = value ?? voices[0]?.id;

  const handlePreview = async () => {
    if (!voiceApiUrl || previewing) return;
    setError(null);
    setPreviewing(true);
    try {
      const blob = await synthesizeSpeech(voiceApiUrl, PREVIEW_TEXT, previewVoice);
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      const audio = audioRef.current ?? new Audio();
      audioRef.current = audio;
      audio.src = url;
      audio.onended = () => setPreviewing(false);
      audio.onerror = () => {
        setError("Couldn't play preview.");
        setPreviewing(false);
      };
      await audio.play();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Preview failed.");
      setPreviewing(false);
    }
  };

  if (!voiceApiUrl) {
    return (
      <div className="space-y-1.5">
        <Label className="text-sm">{label}</Label>
        <p className="text-xs text-muted-foreground">
          Voice service unavailable.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <div className="flex items-center gap-2">
        <Select
          value={value ?? "__default__"}
          onValueChange={(v) => onChange(v === "__default__" ? null : v)}
        >
          <SelectTrigger className="h-8 flex-1 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__default__">{defaultOptionLabel}</SelectItem>
            {voices.map((v) => (
              <SelectItem key={v.id} value={v.id}>
                {v.label}
                {v.accent ? ` · ${v.accent}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={handlePreview}
          disabled={previewing || voices.length === 0}
          aria-label="Preview voice"
          title="Preview voice"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-input text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
        >
          {previewing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Play className="h-4 w-4" />
          )}
        </button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
